<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<template>
    <Head title="Get Started" />
    <BaseDialog v-model:open="showSkipDialog" title="Skip getting to know Nadia">
        <div class="flex flex-col items-center text-center rounded-xl py-10 px-6 w-full md:max-w-[500px] gap-6 border-2 border-[#F5F5F5] shadow-lg bg-white">
            <div class="w-32 h-32">
                <CoachingModeMarble />
            </div>
            <h3 class="title-subsection">Skip getting to know Nadia?</h3>
            <p class="body-small text-valence-grey-750">You won't get a fully tailored version of Nadia right now—but that's okay!</p>
            <p class="body-small text-valence-grey-750">You can always come back and complete this, and Nadia will continue to learn about you over time.</p>
            <div class="w-full flex flex-col items-center gap-4 mt-6">
                <BaseButton class="w-2/5" theme="primary" @click="showSkipDialog = false">Continue</BaseButton>
                <BaseButton data-testid="skip-personalization-dialog" class="w-2/5" theme="tertiary" @click="handleSkip">Skip</BaseButton>
            </div>
        </div>
    </BaseDialog>
    <Transition name="fade" mode="out-in" appear>
        <div
            class="full-height-dynamic safe-area-top-min relative px-3 md:px-6 pt-3 md:pt-6 pb-3 md:pb-6 flex flex-col items-center md:items-stretch gap-8 h-screen w-full overflow-y-auto">
            <div v-if="shouldShowProgressBar" class="top-row place-items-center md:px-4">
                <Transition name="fade">
                    <ProgressBar
                        class="top-row-progress"
                        :current-step="step"
                        :question-index="currentQuestionIndex"
                        :steps="STEP"
                        :is-answered="questionsComponentRef?.isAnswered" />
                </Transition>
                <Transition name="fade">
                    <div v-if="showSkip" class="top-row-skip place-self-end">
                        <BaseButton
                            theme="tertiary"
                            type="button"
                            :disabled="isLoading || isRecordingProcessing"
                            class="pl-0! py-3 pr-2! md:pr-5! disabled:bg-transparent!"
                            data-testid="skip-personalization"
                            @click="showSkipDialog = true">
                            Skip <i class="bi bi-chevron-right icon-bold" aria-hidden="true" />
                        </BaseButton>
                    </div>
                </Transition>
            </div>

            <Bubbles direction="bottom" :is-footer="true" />
            <div class="md:order-1 w-full flex flex-col items-center justify-end md:gap-4 flex-1">
                <!-- Main content -->
                <div class="w-full flex flex-col justify-center items-center gap-4 flex-1">
                    <!-- Small marble - hide during insights loading; also hide on mobile path selection to preserve vertical space above the fold -->
                    <div v-if="!(step === STEP.INSIGHTS && insights.length === 0)" :class="{ 'max-md:hidden': step === STEP.PATH_SELECTION }" class="w-16 h-16">
                        <CoachingModeMarble />
                    </div>
                    <Personalization v-if="step === STEP.PERSONALIZATION" :errors="errors?.personalization" :hris-data="hris_data" @submit="onPersonalizationSubmit" />

                    <OnboardingGreeting
                        v-else-if="step === STEP.GREETING"
                        :greeting-flow="greeting_flow"
                        :first-name="user_details.first_name"
                        :role="role"
                        :organization-name="organizationDetails.is_default ? '' : organizationDetails.name"
                        @get-started="getStarted" />
                    <OnboardingPathSelection v-else-if="step === STEP.PATH_SELECTION" @path-selected="handlePathSelection" />
                    <Questions
                        v-else-if="step === STEP.QUESTIONS"
                        ref="questionsComponentRef"
                        :disabled="isLoading"
                        :is-loading="isLoading"
                        :error="error"
                        :current-question="currentQuestion"
                        :better-voice-composer-enabled="better_voice_composer_enabled"
                        @answer="handleQuestionAnswer"
                        @next="handleNextQuestion"
                        @dismiss-error="error = null"
                        @transcribe-error="handleErrorFallback" />
                    <!-- Loading state for insights generation -->
                    <div v-else-if="step === STEP.INSIGHTS && insights.length === 0" class="flex flex-col gap-10 w-full items-center justify-center">
                        <div class="w-32 h-32">
                            <CoachingModeMarble :is-loading="true" size="big" />
                        </div>
                        <Transition name="message-fade" mode="out-in">
                            <div :key="currentLoadingMessage" class="text-center">
                                <p class="text-valence-grey-750 title-body tracking-[-0.64px]">{{ currentLoadingMessage }}</p>
                            </div>
                        </Transition>
                    </div>
                    <!-- Insights component when loaded -->
                    <Insights v-else-if="step === STEP.INSIGHTS && insights.length > 0" :insights="insights" @done="handleDone" @checkin="handleCheckin" />
                    <ScheduleCheckin
                        v-else-if="step === STEP.CHECKIN"
                        :redirect-path="post_onboarding_redirect"
                        :title="insight_checkin.title || 'Nadia Check-in'"
                        :description="insight_checkin.content || ''" />
                </div>
                <!-- Start of perceived footer -->
                <div class="flex items-center justify-between mt-8 md:my-6 w-full">
                    <div v-if="showPrivacyDialog" :class="isRecordingProcessing || isLoading ? 'hidden md:block' : ''">
                        <PrivacyDialog :disabled="isRecordingProcessing || isLoading" />
                    </div>
                    <img :src="ColorLogo" alt="Valence" class="h-5 w-auto" />
                    <form class="flex flex-col gap-5 items-center" @change.prevent="handleLanguagePreference">
                        <div class="flex items-center justify-center">
                            <select
                                v-model="userLanguage"
                                aria-label="Language Choice"
                                :class="['focus-visible:outline-hidden form-control-select body-small', { 'language-highlight': highlightLanguage }]">
                                <template v-for="opt in language_options" :key="opt[0]">
                                    <option :value="opt[0]" :selected="opt[0] === userLanguage" v-text="opt[0]"></option>
                                </template>
                            </select>
                        </div>
                        <div v-if="errors?.language_preference?.default_language" class="body-small text-red-700">{{ errors.language_preference.default_language }}</div>
                    </form>
                </div>
            </div>
        </div>
    </Transition>
    <CookieBanner />
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { Head, router } from "@inertiajs/vue3";
import ColorLogo from "~img/color-logo.svg";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import Bubbles from "~vue/components/Bubbles.vue";
import CookieBanner from "~vue/components/CookieBanner.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { useStatusBar } from "~vue/composables/useStatusBar";
import Insights from "~vue/Onboarding/Insights.vue";
import OnboardingGreeting from "~vue/Onboarding/OnboardingGreeting.vue";
import OnboardingPathSelection from "~vue/Onboarding/OnboardingPathSelection.vue";
import Questions from "~vue/Onboarding/OnboardingQuestions.vue";
import Personalization from "~vue/Onboarding/Personalization.vue";
import PrivacyDialog from "~vue/Onboarding/PrivacyDialog.vue";
import ProgressBar from "~vue/Onboarding/ProgressBar.vue";
import ScheduleCheckin from "~vue/Onboarding/ScheduleCheckin.vue";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";

useStatusBar();

const STEP = {
    PERSONALIZATION: "personalization",
    GREETING: "greeting",
    PATH_SELECTION: "path_selection",
    QUESTIONS: "questions",
    INSIGHTS: "insights",
    OUTRO: "outro",
    CHECKIN: "checkin",
};

const FADE_DELAY_MS = 500;

const { $setEventContext } = inject("globalProperties");
const props = defineProps({
    errors: Object,
    language_options: Array,
    default_language: String,
    insights: Array,
    step: String,
    event_context: Object,
    user_details: Object,
    post_onboarding_redirect: String,
    role: String,
    insight_checkin: {
        type: { title: String, content: String },
        default: () => ({ title: "", content: "" }),
    },
    script_code: String,
    greeting_flow: Array, // Array of {title, subtitle} objects for multi-page greeting
    hris_data: Object, // HRIS employee data from the HRIS sync
    better_voice_composer_enabled: { type: Boolean, default: false },
    skip_echo: { type: Boolean, default: false },
});

const error = ref(null);
const isLoading = ref(false);
const questionsComponentRef = useTemplateRef("questionsComponentRef");
const userLanguage = ref(props.default_language);
const insights = ref(props.insights || []);
const highlightLanguage = ref(false);

const organizationDetails = inject("organization_details");

// Loading messages that cycle while generating insights
const loadingMessages = ["Considering your responses...", "Reflecting on what you shared...", "Preparing your insights...", "Thinking about your journey..."];
const currentLoadingMessageIndex = ref(0);
const currentLoadingMessage = computed(() => loadingMessages[currentLoadingMessageIndex.value]);
let messageInterval = null;

const questions = ref([
    {
        title: "I can help you best when I understand your work world",
        text: "Tell me more about your day to day? What are you currently working on, and what does your team look like?",
        answer: null,
        llmResponse: null,
        offTopic: false,
    },
    {
        title: "Let's focus on what matters most to you right now",
        text: "Has anyone given you feedback recently that has stuck with you? Or are there specific areas you want to grow in that we can focus on first?",
        answer: null,
        llmResponse: null,
        offTopic: false,
    },
]);

const currentQuestionIndex = ref(0);
const currentQuestion = computed(() => questions.value[currentQuestionIndex.value]);
const showSkip = computed(() => props.step === STEP.QUESTIONS);
const showPrivacyDialog = computed(() => {
    // PrivacyDialog is more integrated in the personalization and outro step, so it's added there.
    return [STEP.PERSONALIZATION, STEP.QUESTIONS, STEP.INSIGHTS, STEP.CHECKIN, STEP.GREETING, STEP.PATH_SELECTION].includes(props.step);
});

// Show progress bar in all steps except when multipage greeting is enabled
// When greeting_flow is empty/null (feature disabled), show progress bar in all steps including GREETING and PATH_SELECTION
const shouldShowProgressBar = computed(() => {
    // Hide during GREETING and PATH_SELECTION steps only if multipage greeting is enabled (greeting_flow has content)
    const isMultipageGreetingEnabled = props.greeting_flow && props.greeting_flow.length > 0;

    if (isMultipageGreetingEnabled && (props.step === STEP.GREETING || props.step === STEP.PATH_SELECTION)) {
        return false;
    }

    // Show for all other cases
    return true;
});

const isRecordingProcessing = computed(() => questionsComponentRef.isProcessing);
const isLastQuestion = computed(() => currentQuestionIndex.value === questions.value.length - 1);

const showSkipDialog = ref(false);

const insightsRequestBody = ref({});

const { data: insightsData, error: insightsFetchError, execute: fetchInsights } = useFetch("/v1/onboarding/insights", { immediate: false }).post(insightsRequestBody).json();

// Watch for insights step and auto-fetch insights if empty
watch(
    () => props.step,
    async (newStep) => {
        if (newStep === STEP.INSIGHTS && insights.value.length === 0) {
            isLoading.value = true;

            // Start cycling through loading messages
            currentLoadingMessageIndex.value = 0;
            messageInterval = setInterval(() => {
                currentLoadingMessageIndex.value = (currentLoadingMessageIndex.value + 1) % loadingMessages.length;
            }, 3000); // Change message every 3 seconds

            try {
                await fetchInsights();

                if (insightsFetchError.value) {
                    throw new Error(insightsFetchError.value.message || insightsFetchError.value);
                }

                if (insightsData.value?.error) {
                    throw new Error(insightsData.value.error);
                }

                if (insightsData.value?.insights) {
                    insights.value = insightsData.value.insights;
                } else {
                    throw new Error("Unexpected response structure");
                }
            } catch (e) {
                logError(e);
                // Show error alert and allow retry
                alert("Failed to generate insights. Please refresh the page to try again.");
            } finally {
                isLoading.value = false;
                // Stop cycling messages when done
                if (messageInterval) {
                    clearInterval(messageInterval);
                    messageInterval = null;
                }
            }
        }
    },
    { immediate: true }, // Run immediately in case we're already on insights step
);

onMounted(() => {
    $setEventContext(props.event_context);

    // Briefly highlight the language dropdown to draw attention to it
    highlightLanguage.value = true;
    setTimeout(() => (highlightLanguage.value = false), 3000);
});

onUnmounted(() => {
    $setEventContext({});
    // Clean up message interval
    if (messageInterval) {
        clearInterval(messageInterval);
        messageInterval = null;
    }
});

function handleNextQuestion() {
    if (isLastQuestion.value) {
        isLoading.value = true;
        questions.value[currentQuestionIndex.value].showMemoryAnimation = true;
        /*
         * Allow the saved memory animation to run completely
         * Submit via Inertia to save questions and navigate to insights step
         */
        postResponse(
            {
                intent: "insights",
                form: questions.value.map((q) => ({ question: q.text, answer: q.answer })),
            },
            {
                only: ["step", "insights", "errors"],
                onFinish: () => {
                    isLoading.value = false;
                },
            },
        );
    } else {
        nextTick(() => {
            questions.value[currentQuestionIndex.value].showMemoryAnimation = true;
            currentQuestionIndex.value++;
        });
    }
}

async function handleQuestionAnswer({ answer, append, onFinish, usingMic }) {
    let nextAnswer;
    isLoading.value = true;
    error.value = null;

    const currentAnswer = questions.value[currentQuestionIndex.value].answer;
    if (append && currentAnswer !== null) {
        nextAnswer = `${currentAnswer}\n\n${answer}`;
    } else {
        logUserInteraction(`onboarding_q${currentQuestionIndex.value + 1}_completed`, { mic: usingMic, skip_echo: props.skip_echo });
        nextAnswer = answer;
    }

    if (props.skip_echo) {
        questions.value[currentQuestionIndex.value].answer = nextAnswer;
        questions.value[currentQuestionIndex.value].offTopic = false;
        isLoading.value = false;
        onFinish();
        handleNextQuestion();
        return;
    }

    try {
        const response = await fetch("/coach/active_listening", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: JSON.stringify({
                form: {
                    question: questions.value[currentQuestionIndex.value].text,
                    answer: nextAnswer,
                },
            }),
        });

        if (!response.ok) {
            throw new Error("Failed to obtain active_listening response");
        }

        const payload = await response.json();

        if (payload.status === "guardrails_violated") {
            questions.value[currentQuestionIndex.value].offTopic = true;
            return;
        }

        questions.value[currentQuestionIndex.value].answer = nextAnswer;
        questions.value[currentQuestionIndex.value].llmResponse = payload.content;
        questions.value[currentQuestionIndex.value].offTopic = false;
    } catch (e) {
        logError(e);
        handleErrorFallback(new Error("I am having trouble processing your answer. Please try again"));
    } finally {
        isLoading.value = false;
        onFinish();
    }
}

function handleErrorFallback(e) {
    error.value = e;
    logError(e);
}

function postResponse(payload, { only = [], onFinish = () => {}, errorBag } = {}) {
    isLoading.value = true;

    router.post("", payload, {
        only,
        // Only need to load this once in the first request
        except: ["language_options"],
        errorBag,
        headers: {
            "X-CSRFToken": getCookie("csrftoken"),
        },
        onFinish: () => {
            isLoading.value = false;
            onFinish();
        },
    });
}

function handleDone({ onFinish } = {}) {
    postResponse({ intent: "done" }, { only: ["step", "user_details"], onFinish });
}

function handleCheckin({ insight_checkin, onFinish }) {
    postResponse({ intent: "checkin", insight_checkin }, { only: ["step", "user_details", "insight_checkin", "post_onboarding_redirect"], onFinish });
}

function handleSkip() {
    postResponse({ intent: "skip" });
}

function onPersonalizationSubmit(formValues) {
    // Save language preference alongside personalization so it's persisted before the user moves on
    handleLanguagePreference();
    postResponse(
        { intent: "personalization", form: formValues, script_code: props.script_code },
        { only: ["step", "errors", "user_details", "role", "script_code", "greeting_flow"], errorBag: "personalization" },
    );
}

function getStarted() {
    postResponse({ intent: "get_started" });
}

function handlePathSelection(selectedPath) {
    postResponse(
        {
            intent: "path_selection",
            form: { onboarding_path_choice: selectedPath },
        },
        {
            only: ["step", "errors", "post_onboarding_redirect"],
        },
    );
}

function handleLanguagePreference() {
    postResponse({ intent: "language_preference", form: { default_language: userLanguage.value } }, { only: ["errors", "default_language"], errorBag: "language_preference" });
}
</script>

<style type="postcss">
@reference "../../../css/tailwind.css";

.error-list li {
    @apply details text-red-700 text-left;
}

.button {
    @apply flex justify-center items-center gap-1 transition-colors ease-in duration-150 rounded-3xl px-9 py-3 leading-none body-bold w-full ring-2 ring-inset;
}

.button-text {
    @apply md:px-6 px-0 py-3 transition-colors ease-in duration-150 body-semibold leading-none ring-0;
}

.button-text:not(.button-text--muted) {
    @apply text-valence-primary-500 hover:text-[#4B508F];
}

.button-text--muted {
    @apply hover:text-valence-grey-800 text-valence-grey-750;
}

.button-primary {
    @apply bg-valence-primary-500 hover:bg-[#4B508F] hover:ring-[#4B508F] text-white ring-valence-primary-500;
}

.button-danger {
    @apply text-white ring-[#CF1322] bg-[#CF1322];
}

.button-primary:disabled,
.button-danger:disabled {
    @apply bg-[#F5F5F5] ring-[#F5F5F5] text-valence-grey-750;
}

.button-text:disabled {
    @apply opacity-40;
}

@media (min-width: 768px) {
    .gradient-background {
        background: linear-gradient(360deg, #e9defa 0%, #fff6eb 100%);
    }
}
</style>

<style scoped>
@reference "../../../css/tailwind.css";
.fade-enter-active,
.fade-leave-active {
    transition: opacity v-bind(FADE_DELAY_MS + "ms") ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Message fade transition for cycling loading messages */
.message-fade-enter-active,
.message-fade-leave-active {
    transition: opacity 600ms ease;
}

.message-fade-enter-from,
.message-fade-leave-to {
    opacity: 0;
}

.language-highlight {
    animation: language-pulse 1.5s ease-in-out 2;
    border-radius: 8px;
}

@keyframes language-pulse {
    0%,
    100% {
        background-color: transparent;
    }
    50% {
        background-color: rgba(99, 102, 241, 0.15);
    }
}

/* The following are needed for the language dropdown in the "footer" */
.form-control {
    @apply flex flex-col gap-1;
}

.form-control-select {
    @apply bg-transparent shrink-0 inline-block w-auto hover:text-valence-grey-800 text-valence-grey-750;
}

.form-control-select option {
    @apply text-gray-700;
}

.top-row {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: repeat(1, 1fr);
    gap: 20px;
}

.top-row-progress {
    grid-column: span 6 / span 6;
    grid-column-start: 4;
    grid-row-start: 1;
}

.top-row-skip {
    grid-column: span 3 / span 3;
    grid-column-start: 10;
    grid-row-start: 1;
}
</style>
